import { getEventCategoryFromLocation } from './eventCategories';
import { componentEvents } from './componentEvents';
import { isObject } from '../../object';
import { sendEvent } from '@local/services/webApi';
import { jsonpath } from '../../jsonpath';
import ReactGA from 'react-ga4';

const isLoggingEnabled = __IS_AZURE_EVENT_HUBS_ENABLED__ === 'true';

function processCategoryOverride(event, eventCategory) {
  const foundCategoryOverride = event?.categoryOverride?.[eventCategory?.eventprefix];

  // If categoryOverride is set as null, overwrite default event to null
  if (!isObject(event) || (isObject(event) && foundCategoryOverride === null)) {
    return null;
  }

  // If foundCategoryOverride is simply missing from event, then return event as default
  // Also ensure foundCategoryOverride is an object before spread operator
  if (foundCategoryOverride !== null && !isObject(foundCategoryOverride)) {
    return event;
  }

  const finalEvent = {
    ...event,
    ...foundCategoryOverride
  };

  // Remove categoryOverrides from final event
  delete finalEvent.categoryOverride;

  return finalEvent;
}

function getComponentEvent(componentName, eventName, eventCategory) {
  const component = componentEvents[componentName];
  const compEvent = component?.events[eventName];

  if (compEvent) {
    return {
      componentName: componentName,
      componentCode: component.code,
      eventName: eventName,
      eventHub: processCategoryOverride(compEvent.eventHub, eventCategory),
      ga: processCategoryOverride(compEvent.ga, eventCategory)
    };
  }
  return null;
}

function processEventHub(eventCategory, event, options) {
  const sessionId = sessionStorage.getItem('sessionId') || '';
  const idToken = sessionStorage.getItem('idToken') || '';
  const data = options?.data;
  const eventCode = options?.eventCode;

  sendEvent({
    sessionId,
    idToken,
    eventCategory,
    componentName: event.componentName,
    eventName: event.eventName,
    eventCode,
    data
  }).catch(() => {});
}

function fillActionWithData(action, data) {
  return action?.replace(/\[([^\]]+)\]/g, (match, path) => {
    const value = jsonpath.getValue(data, path);
    if (value === undefined) {
      return match; // Return the original match if the path is not found
    }
    return value;
  });
}

function processGA(eventCategory, event, options) {
  let action = event?.ga?.action;

  if (options?.data) {
    // Fill GA Action with Data from Option
    action = fillActionWithData(action, options.data);
  }

  ReactGA.event({
    category: eventCategory?.category,
    action: action,
    label: eventCategory?.label
  });
}

// This function pushes the GA event to the dataLayer for Google Tag Manager (GTM) tracking
function pushGAToDataLayer(eventCategory, event, options) {
  if (!eventCategory || !event || !window.dataLayer) {
    return;
  }

  const action = options?.data ? fillActionWithData(event?.ga?.action, options.data) : event?.ga?.action;

  const dataLayerEvent = {
    eventCategory: eventCategory?.category,
    eventAction: action,
    eventLabel: eventCategory?.label,
    ...options?.data
  };

  window.dataLayer.push(dataLayerEvent);
}

export function logComponentEvent(componentName, eventName, options) {
  if (componentName && eventName && isLoggingEnabled) {
    const eventCategory = options?.eventCategory ?? getEventCategoryFromLocation();
    const event = getComponentEvent(componentName, eventName, eventCategory);

    if (event?.eventHub) {
      processEventHub(eventCategory, event, options);
    }

    if (event?.ga) {
      processGA(eventCategory, event, options);
      if (event?.ga?.pushToDataLayer) {
        pushGAToDataLayer(eventCategory, event, options);
      }
    }
  }
}
